'use client';

import {
  ModButton,
  ModModal,
  ModModalContent
} from '@moderneinc/design-system-components';
import {
  semanticColors,
  spacing,
  typography
} from '@moderneinc/design-system-tokens';
import { Box, ButtonBase, Stack, Typography } from '@mui/material';
import type { FunctionComponent } from 'react';
import { useTourEngineStore } from '../../stores/tour-engine.store';
import { DAY_ZERO_TOUR_ID } from './day-zero-tour.config';

// The Moderne mark, inlined so it inherits `currentColor` (see ModerneLogo.svg).
const MODERNE_MARK_PATH =
  'M20.472 3.035a.45.45 0 0 0-.49.097l-7.723 7.724c-.508-4.414-4.263-7.855-8.811-7.855a.45.45 0 0 0-.45.448v16.852c0 .008.004.014.004.022q.002.031.01.064.005.035.016.066l.005.02c.005.013.015.023.021.035l.036.06a.4.4 0 0 0 .043.047.4.4 0 0 0 .048.044l.059.035q.016.012.036.022l.016.003q.074.029.156.031h16.851a.45.45 0 0 0 .45-.449V3.45a.45.45 0 0 0-.278-.414Zm-.621 1.497v6.894h-6.895zm0 11.108h-7.53v-3.317h7.53zM3.896 19.218v-6.894h6.895l-3.447 3.447zm0-15.308a7.99 7.99 0 0 1 7.516 7.516H3.896zm4.082 12.496 3.447-3.447v6.894H4.53zm4.344.13h7.53v3.317h-7.53z';

/**
 * Intro modal shown when the Day Zero tour is launched (status 'welcome'),
 * before the first step. "Show me how" starts the org-selector step; the
 * secondary action lets the user opt out (and restart later from the Help menu).
 */
export const TourWelcomeModal: FunctionComponent = () => {
  const status = useTourEngineStore((state) => state.status);
  const activeTourId = useTourEngineStore((state) => state.activeTourId);
  const begin = useTourEngineStore((state) => state.begin);
  const dismiss = useTourEngineStore((state) => state.dismiss);
  const open = status === 'welcome' && activeTourId === DAY_ZERO_TOUR_ID;

  return (
    <ModModal open={open} onClose={dismiss} maxWidth="sm" fullWidth>
      <ModModalContent>
        <Stack
          sx={{
            alignItems: 'center',
            textAlign: 'center',
            gap: `${spacing.spacing_2}px`,
            px: { xs: 1, sm: `${spacing.spacing_4}px` },
            py: `${spacing.spacing_3}px`
          }}
        >
          <Box
            component="svg"
            viewBox="0 0 24 24"
            aria-hidden="true"
            sx={{ width: 48, height: 48, color: 'text.primary' }}
          >
            <path fill="currentColor" d={MODERNE_MARK_PATH} />
          </Box>
          <Typography
            sx={{
              fontFamily: typography.fontFamily.heading,
              fontSize: typography.fontSize.h1,
              fontWeight: typography.fontWeight.bold,
              lineHeight: 1.2
            }}
          >
            Welcome to Moderne
          </Typography>
          <Typography
            variant="body"
            sx={{ color: semanticColors.color.text.secondary }}
          >
            Moderne runs automated code transformations across all your
            repositories at once. Let&apos;s get you to your first result in a
            few minutes.
          </Typography>
          <ModButton
            variant="primary"
            size="medium"
            autoFocus
            onClick={begin}
            sx={{ mt: 1, alignSelf: 'stretch' }}
          >
            Show me how
          </ModButton>
          <ButtonBase
            onClick={dismiss}
            sx={{ borderRadius: `${spacing.spacing_1_2}px`, px: 1, py: 0.5 }}
          >
            <Typography
              variant="bodySm"
              sx={{ color: semanticColors.color.text.secondary }}
            >
              I&apos;ll explore on my own — restart from Help anytime
            </Typography>
          </ButtonBase>
        </Stack>
      </ModModalContent>
    </ModModal>
  );
};
